/**
 * 중앙화된 로깅 유틸리티
 * 운영 환경에서는 console.log를 비활성화하고 에러 트래킹 서비스로 전송
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  environment: string;
  logLevel: LogLevel;
  enableConsole: boolean;
  sentryDsn?: string;
}

class Logger {
  private config: LoggerConfig;
  private logLevels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  constructor() {
    this.config = {
      environment: process.env.REACT_APP_ENVIRONMENT || 'development',
      logLevel: (process.env.REACT_APP_LOG_LEVEL as LogLevel) || 'info',
      enableConsole: process.env.REACT_APP_ENVIRONMENT !== 'production',
      sentryDsn: process.env.REACT_APP_SENTRY_DSN,
    };

    // Sentry 초기화 (DSN이 있는 경우)
    if (this.config.sentryDsn && typeof window !== 'undefined') {
      // Sentry는 별도로 설치 필요: npm install @sentry/react
      this.initSentry();
    }
  }

  private initSentry() {
    // Sentry 초기화 로직
    // import * as Sentry from '@sentry/react';
    // Sentry.init({
    //   dsn: this.config.sentryDsn,
    //   environment: this.config.environment,
    // });
  }

  private shouldLog(level: LogLevel): boolean {
    return this.logLevels[level] >= this.logLevels[this.config.logLevel];
  }

  private sanitizeData(data: any): any {
    // 민감한 정보 제거
    if (typeof data === 'object' && data !== null) {
      const sanitized = { ...data };
      const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'auth_id', 'anon_key'];
      
      Object.keys(sanitized).forEach(key => {
        if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive.toLowerCase()))) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof sanitized[key] === 'object') {
          sanitized[key] = this.sanitizeData(sanitized[key]);
        }
      });
      
      return sanitized;
    }
    return data;
  }

  private formatMessage(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const sanitizedData = data ? this.sanitizeData(data) : null;
    
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${
      sanitizedData ? ` | ${JSON.stringify(sanitizedData)}` : ''
    }`;
  }

  debug(message: string, data?: any) {
    if (this.shouldLog('debug') && this.config.enableConsole) {
      console.log(this.formatMessage('debug', message, data));
    }
  }

  info(message: string, data?: any) {
    if (this.shouldLog('info') && this.config.enableConsole) {
      console.info(this.formatMessage('info', message, data));
    }
  }

  warn(message: string, data?: any) {
    if (this.shouldLog('warn')) {
      if (this.config.enableConsole) {
        console.warn(this.formatMessage('warn', message, data));
      }
      // 운영 환경에서는 모니터링 서비스로 전송
      this.sendToMonitoring('warn', message, data);
    }
  }

  error(message: string, error?: any, data?: any) {
    if (this.shouldLog('error')) {
      const errorData = {
        message: error?.message || error,
        stack: error?.stack,
        ...data,
      };
      
      if (this.config.enableConsole) {
        console.error(this.formatMessage('error', message, errorData));
      }
      
      // 운영 환경에서는 에러 트래킹 서비스로 전송
      this.sendToErrorTracking(message, error, data);
    }
  }

  private sendToMonitoring(level: LogLevel, message: string, data?: any) {
    // 운영 환경에서 모니터링 서비스로 로그 전송
    if (this.config.environment === 'production') {
      // 예: Datadog, CloudWatch, etc.
      // 실제 구현은 사용하는 서비스에 따라 다름
    }
  }

  private sendToErrorTracking(message: string, error?: any, data?: any) {
    // 운영 환경에서 에러 트래킹 서비스로 전송
    if (this.config.environment === 'production' && this.config.sentryDsn) {
      // Sentry로 에러 전송
      // import * as Sentry from '@sentry/react';
      // Sentry.captureException(error, {
      //   extra: { message, ...data },
      // });
    }
  }

  // 성능 측정 유틸리티
  measurePerformance(label: string, fn: () => void | Promise<void>) {
    const start = performance.now();
    const result = fn();
    
    if (result instanceof Promise) {
      return result.finally(() => {
        const duration = performance.now() - start;
        this.debug(`Performance: ${label}`, { duration: `${duration.toFixed(2)}ms` });
      });
    } else {
      const duration = performance.now() - start;
      this.debug(`Performance: ${label}`, { duration: `${duration.toFixed(2)}ms` });
      return result;
    }
  }
}

// 싱글톤 인스턴스
export const logger = new Logger();

// 개발 환경에서만 전역 변수로 노출 (디버깅용)
if (process.env.NODE_ENV === 'development') {
  (window as any).logger = logger;
}